import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, Loader2, Mail } from "lucide-react";
import { GLOBAL_IMAGES } from "../assets/asset";
import { useMutation } from "@tanstack/react-query";
import {
  startJourney,
  logSignupSubmitted,
  logSignupApiResult,
  logEmailVerificationSent,
} from "../utils/datadogLogger";
import {
  extractStudentEmail,
  extractStudentPreAuthToken,
  isAlreadyVerifiedResponse,
  isPendingStudentVerificationResponse,
  studentAuthApi,
} from "../features/auth/authApi";
import { createStudentVerificationFlow } from "../features/auth/studentOtpFlow";

const GENERIC_SIGNUP_ERROR = "Unable to complete the request right now.";
const GENERIC_VERIFICATION_SENT_MESSAGE = "Verification code sent to your email.";
const GENERIC_ALREADY_VERIFIED_MESSAGE = "This account is already verified.";

const CreateAccountForm = () => {
  const navigate = useNavigate();
  const signupMutation = useMutation({
    mutationKey: ["signup"],
    mutationFn: (data) => studentAuthApi.signupV2(data),
  });

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerificationAlert, setShowVerificationAlert] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    const hasMinLength = password.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: hasMinLength && hasLetter && hasNumber && hasSpecialChar,
      requirements: {
        minLength: hasMinLength,
        hasLetter,
        hasNumber,
        hasSpecialChar,
      },
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleResendVerification = () => {
    studentAuthApi
      .resendSignupOtp({ email: formData.email.trim() })
      .then((response) => {
        const nextContext = createStudentVerificationFlow({
          email: extractStudentEmail(response) || formData.email,
          preAuthToken: extractStudentPreAuthToken(response),
          verificationType: response?.data?.data?.verification_type || "otp",
          source: "signup",
        });

        navigate("/verification", {
          state: { email: nextContext.email, source: "signup" },
        });
      })
      .catch((error) => {
        setErrors({
          submit: GENERIC_SIGNUP_ERROR,
        });
      });
  };

  const handleSignup = async () => {

    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    const passwordValidation = validatePassword(formData.password);
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!passwordValidation.isValid) {
      newErrors.password = "Password must meet all requirements";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Log signup submitted
    logSignupSubmitted(formData.email, 'web');

    const payload = {
      email: formData.email.trim().toLowerCase(),
      password: formData.password,
    };

    signupMutation.mutate(payload, {
      onSuccess: async (data) => {
        console.log("suuccccess");
        // Log signup API success
        logSignupApiResult(
          formData.email,
          data?.status || 200,
          { email: formData.email }, // Masked request body
          { success: true } // Masked response body
        );

        // Log email verification sent
        logEmailVerificationSent(formData.email);

        const nextContext = createStudentVerificationFlow({
          email: extractStudentEmail(data) || payload.email,
          preAuthToken: extractStudentPreAuthToken(data),
          verificationType: data?.data?.data?.verification_type || "otp",
          accountType: data?.data?.data?.account_type || "student",
          source: "signup",
        });

        navigate("/verification", {
          state: {
            email: nextContext.email,
            source: "signup",
            successMessage: GENERIC_VERIFICATION_SENT_MESSAGE,
          },
        });
      },
      onError: async (error) => {
        const errorData = error?.response?.data || {};
        const errorMessage = (errorData.message || "").trim();
        const statusCode = String(errorData.status_code || error?.response?.status || "");

        // Log signup API error
        logSignupApiResult(
          formData.email,
          error?.response?.status || 500,
          { email: formData.email }, // Masked request body
          errorData, // Response body
          errorMessage
        );

        if (isAlreadyVerifiedResponse(errorData)) {
          setErrors({
            submit: GENERIC_ALREADY_VERIFIED_MESSAGE,
          });
          return;
        }

        if (isPendingStudentVerificationResponse(errorData)) {
          try {
            const resendResponse = await studentAuthApi.resendSignupOtp({
              email: payload.email,
            });
            const nextContext = createStudentVerificationFlow({
              email: extractStudentEmail(resendResponse) || payload.email,
              preAuthToken: extractStudentPreAuthToken(resendResponse),
              verificationType:
                resendResponse?.data?.data?.verification_type || "otp",
              source: "signup",
            });

            navigate("/verification", {
              state: {
                email: nextContext.email,
                source: "signup",
                successMessage: GENERIC_VERIFICATION_SENT_MESSAGE,
              },
            });
          } catch (resendError) {
            const resendErrorData = resendError?.response?.data || {};

            if (isAlreadyVerifiedResponse(resendErrorData)) {
              setErrors({
                submit: GENERIC_ALREADY_VERIFIED_MESSAGE,
              });
              return;
            }

            createStudentVerificationFlow(
              {
                email: payload.email,
                source: "signup",
                expiresAt: Date.now(),
              },
              { resetCycle: false }
            );
            setShowVerificationAlert(true);
            setErrors({
              submit: GENERIC_SIGNUP_ERROR,
            });
          }
        } else {
          const submitMessage =
            errorMessage === "User Already Exist"
              ? "Unable to complete the request right now."
              : GENERIC_SIGNUP_ERROR;

          setErrors({
            submit: submitMessage,
          });
        }
      },
    });
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute top-6 left-6">
        <img
          src={GLOBAL_IMAGES.dingo}
          alt="Mathdingo Logo"
          className="w-20 h-20"
        />
      </div>

      <div className="w-full max-w-xl px-4">
        {showVerificationAlert ? (
          <div className="bg-white/60 rounded-3xl p-8 shadow-lg">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <Mail size={64} className="text-blue-600" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Verification Required</h2>
              <p className="text-lg mb-6">
                We've already sent a verification email to <strong>{formData.email}</strong>. 
                Please check your inbox and verify your account before logging in.
              </p>
              <p className="text-md mb-8">
                If you can't find the email, check your spam folder or click the button below to resend.
              </p>
              <div className="flex flex-col gap-4">
                <button
                  onClick={handleResendVerification}
                  className="w-full bg-blue-800 text-white py-3 rounded-full hover:bg-blue-900 transition-colors text-xl font-semibold"
                >
                  Resend Verification Email
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="text-blue-800 text-xl font-semibold hover:underline"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white/60 rounded-3xl p-8 shadow-lg">
            <div className="flex items-center gap-4 mb-8">
              <img
                src={GLOBAL_IMAGES.LoginBackButton}
                alt="Back"
                className="w-14 h-14 cursor-pointer"
                onClick={() => navigate(-1)}
              />
              <h2 className="text-4xl font-bold">Create your account</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-xl font-bold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  autoComplete="off"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500"
                  placeholder="sharma@mathdingo.com.au"
                />
                {errors.email && (
                  <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>{errors.email}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xl font-bold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    autoComplete="off"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-full border-2 ${
                      errors.password ? "border-red-300" : "border-gray-300"
                    } pr-12 focus:outline-none focus:border-blue-500`}
                    placeholder="********"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={24} /> : <Eye size={24} />}
                  </button>
                </div>
                {errors.password && (
                  <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>
                      Minimum 8 characters, with letters, numbers and special
                      characters
                    </span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xl font-bold text-gray-700 mb-2">
                  Re-enter Password
                </label>
                <div className="relative">
                  <input
                    autoComplete="off"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-full border-2 ${
                      errors.confirmPassword
                        ? "border-red-300"
                        : "border-gray-300"
                    } pr-12 focus:outline-none focus:border-blue-500`}
                    placeholder="********"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={24} />
                    ) : (
                      <Eye size={24} />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>{errors.confirmPassword}</span>
                  </div>
                )}
              </div>

              <button
                onClick={handleSignup}
                disabled={signupMutation.isPending}
                className="w-full bg-blue-800 text-white py-3 rounded-full hover:bg-blue-900 transition-colors text-xl font-semibold flex items-center justify-center"
              >
                {signupMutation.isPending ? (
                  <Loader2 className="animate-spin" size={24} />
                ) : (
                  "Signup"
                )}
              </button>

              <div className="text-center mt-4">
                <p className="text-xl text-gray-700 mb-2">Already registered?</p>
                <button
                  onClick={() => navigate("/login")}
                  className="text-blue-800 text-xl font-semibold hover:underline"
                >
                  Login
                </button>
              </div>

              {errors.submit && (
                <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 rounded-full text-red-700 text-sm">
                  <AlertCircle size={16} />
                  <span>{errors.submit}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateAccountForm;
